import React from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId, useAuthentication } from "@/contexts"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import { useAdminSitePageState } from "@/modules/admin/sites"
import { useGetBuildingTypes } from "@/services/site"
import { useUrl } from "@/utils/url"

import { Button, Link as MuiLink, Stack } from "@mui/material"

import { AdminSiteEditForm } from "./admin-site-edit-form"

export const AdminSiteEditRoute: React.FC = () => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { t } = useTranslation("common")
  const { activeOrganizationId } = useActiveOrganizationId()
  const { canEditSite } = useAuthentication()
  const { adminSitePath, adminSitesPath } = useUrl()
  const { siteData, siteId, siteIsFetching, siteIsLoading, siteIsUnavailable } =
    useAdminSitePageState()

  const organizationId = activeOrganizationId ?? ""
  const areAddressFieldsReadOnly = canEditSite !== true

  const { buildingTypesData, buildingTypesIsPending } = useGetBuildingTypes({
    queryOptions: {
      enabled: Boolean(organizationId),
    },
  })

  if (!organizationId) {
    return null
  }

  if (siteIsUnavailable) {
    return <Page404 />
  }

  if (siteIsLoading) {
    return (
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <MuiLink
              component={Link}
              to={`${adminSitesPath}${search}`}
              underline="hover"
            >
              {t("admin.sites")}
            </MuiLink>
          }
          heading={t("editSite.title")}
          primaryActions={
            <Button
              onClick={() => {
                void navigate(`${adminSitePath(String(siteId))}${search}`, {
                  replace: true,
                })
              }}
              variant="outlined"
            >
              {t("button.cancel")}
            </Button>
          }
        />
        <PageCard>
          <AdminEntityFieldsSkeleton
            ariaLabel={t("loadingAriaLabel", {
              title: t("admin.siteDetail.title"),
            })}
            roundedInputs
          />
        </PageCard>
      </Stack>
    )
  }

  if (!siteData || siteId === undefined) {
    return null
  }

  return (
    <AdminSiteEditForm
      areAddressFieldsReadOnly={areAddressFieldsReadOnly}
      buildingTypes={buildingTypesData ?? []}
      buildingTypesIsPending={buildingTypesIsPending}
      site={siteData}
      siteId={siteId}
      siteIsFetching={siteIsFetching}
    />
  )
}
